// src/pages/trading-portal/historical/HistoricalDataPage.js
// Historical price data browser

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    Chip,
    Grid,
    Alert,
    CircularProgress,
    Autocomplete,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    CandlestickChart as CandlestickIcon,
    CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import { TimeFrame, TimeFrameLabels } from '../stockTradingEnums';

const HistoricalDataPage = () => {
    const location = useLocation();
    const preSelectedSymbolId = location.state?.symbolId || null;
    const hasAutoSelected = useRef(false);

    // Permission checks
    const [canView, setCanView] = useState(false);

    // State
    const [symbols, setSymbols] = useState([]);
    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [timeframe, setTimeframe] = useState('DAILY');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [priceData, setPriceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSymbols, setLoadingSymbols] = useState(true);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [fetching, setFetching] = useState(false);
    const [importing, setImporting] = useState(false);

    useEffect(() => {
        const checkPerms = async () => {
            const view = await hasPermission('tr-historical', 'view');
            setCanView(view);
        };
        checkPerms();
    }, []);

    // Fetch watchlist symbols for autocomplete
    const fetchSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        } finally {
            setLoadingSymbols(false);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchSymbols();
    }, [fetchSymbols, canView]);

    // Auto-select symbol if navigated from watchlist
    useEffect(() => {
        if (preSelectedSymbolId && symbols.length > 0 && !hasAutoSelected.current) {
            const match = symbols.find(s => s.id === preSelectedSymbolId);
            if (match) {
                setSelectedSymbol(match);
                hasAutoSelected.current = true;
            }
        }
    }, [preSelectedSymbolId, symbols]);

    // Fetch historical prices
    const fetchPrices = useCallback(async () => {
        if (!canView || !selectedSymbol) return;
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: page + 1,
                per_page: rowsPerPage,
                timeframe: timeframe,
            });
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);

            const response = await axiosInstance.get(`/api/stock-trading/symbols/${selectedSymbol.id}/price-history?${params.toString()}`);

            if (response.data.success) {
                setPriceData(response.data.data || []);
                setTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch price data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch price data');
        } finally {
            setLoading(false);
        }
    }, [canView, selectedSymbol, page, rowsPerPage, timeframe, startDate, endDate]);

    useEffect(() => {
        if (selectedSymbol) fetchPrices();
    }, [fetchPrices, selectedSymbol]);

    // Fetch historical data from provider and store in DB
    const handleFetchFromProvider = async () => {
        if (!selectedSymbol) return;
        setFetching(true);
        setError(null);
        setInfo(null);
        try {
            const intervalMap = { 'THIRTY_SEC': '30sec', 'ONE_MIN': '1min', 'TWO_MIN': '2min', 'THREE_MIN': '3min', 'FOUR_MIN': '4min', 'FIVE_MIN': '5min', 'FIFTEEN_MIN': '15min', 'ONE_HOUR': '60min', 'TWO_HOUR': '120min', 'FOUR_HOUR': '240min', 'FIVE_HOUR': '300min', 'DAILY': 'daily', 'WEEKLY': 'weekly', 'MONTHLY': 'monthly' };
            const payload = {
                symbol_id: selectedSymbol.id,
                interval: intervalMap[timeframe] || 'daily',
                start_date: startDate || undefined,
                end_date: endDate || undefined,
            };
            const response = await axiosInstance.post('/api/stock-trading/import-historical', payload);
            const imported = response.data?.data?.entries_imported ?? 0;
            const fetched = response.data?.data?.total_fetched ?? 0;
            if (imported > 0) {
                setInfo(`Imported ${imported} new ${timeframe} bars (${fetched} returned by provider).`);
            } else if (fetched > 0) {
                setInfo(`Provider returned ${fetched} bars but all were already in the database.`);
            } else {
                setInfo(`Provider returned 0 bars for ${timeframe}. Try a more recent date range — sub-minute timeframes have limited historical retention.`);
            }
            fetchPrices();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch prices from provider');
        } finally {
            setFetching(false);
        }
    };

    // Import historical data
    const handleImport = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !selectedSymbol) return;
        setImporting(true);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('timeframe', timeframe);

        try {
            await axiosInstance.post(`/api/stock-trading/symbols/${selectedSymbol.id}/import-prices`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            fetchPrices();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to import data');
        } finally {
            setImporting(false);
            event.target.value = '';
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
    const handleTimeframeChange = (event, newTimeframe) => { if (newTimeframe) { setTimeframe(newTimeframe); setPage(0); } };

    const formatPrice = (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-';
    const formatVolume = (vol) => vol != null ? parseInt(vol).toLocaleString() : '-';

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view historical data.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ mb: 2 }}>Historical Data</Typography>
            </Box>

            {/* Controls */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    {/* Symbol Selector */}
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            options={symbols}
                            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                            value={selectedSymbol}
                            onChange={(e, newValue) => { setSelectedSymbol(newValue); setPage(0); }}
                            loading={loadingSymbols}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Symbol"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingSymbols ? <CircularProgress size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Date Range */}
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Button
                                variant="contained"
                                startIcon={fetching ? <CircularProgress size={18} color="inherit" /> : <CloudDownloadIcon />}
                                onClick={handleFetchFromProvider}
                                disabled={!selectedSymbol || fetching}
                                sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                            >
                                Fetch Data
                            </Button>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={importing ? <CircularProgress size={18} /> : <DownloadIcon />}
                                disabled={!selectedSymbol || importing}
                            >
                                Import
                                <input type="file" accept=".csv" hidden onChange={handleImport} />
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Timeframe Selector */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Timeframe</Typography>
                    <ToggleButtonGroup
                        value={timeframe}
                        exclusive
                        onChange={handleTimeframeChange}
                        size="small"
                    >
                        {Object.entries(TimeFrame).map(([key, value]) => (
                            <ToggleButton key={key} value={value} sx={{ textTransform: 'none', px: 2 }}>
                                {TimeFrameLabels[value]}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
            {info && <Alert severity={info.startsWith('Imported') ? 'success' : 'info'} sx={{ mb: 3 }} onClose={() => setInfo(null)}>{info}</Alert>}

            {/* Data Table */}
            {!selectedSymbol ? (
                <Paper sx={{ py: 6, textAlign: 'center' }}>
                    <CandlestickIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>Select a Symbol</Typography>
                    <Typography color="text.secondary" variant="body2">
                        Choose a symbol from your watchlist to view historical price data
                    </Typography>
                </Paper>
            ) : (
                <Paper>
                    <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">{selectedSymbol.symbol}</Typography>
                            <Chip label={TimeFrameLabels[timeframe]} size="small" color="primary" variant="outlined" />
                            <Chip label={`${totalCount} records`} size="small" />
                        </Box>
                        <IconButton onClick={fetchPrices}><RefreshIcon /></IconButton>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Open</TableCell>
                                    <TableCell align="right">High</TableCell>
                                    <TableCell align="right">Low</TableCell>
                                    <TableCell align="right">Close</TableCell>
                                    <TableCell align="right">Volume</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                    </TableRow>
                                ) : priceData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No price data available</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Click "Fetch Data" to retrieve prices from the provider
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    priceData.map((row, index) => {
                                        const isPositive = (row.close_price || 0) >= (row.open_price || 0);
                                        return (
                                            <TableRow key={row.id || index} hover>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {row.timestamp ? (timeframe === 'THIRTY_SEC' ? new Date(row.timestamp).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ['ONE_MIN','TWO_MIN','THREE_MIN','FOUR_MIN','FIVE_MIN','FIFTEEN_MIN','ONE_HOUR','TWO_HOUR','FOUR_HOUR','FIVE_HOUR'].includes(timeframe) ? new Date(row.timestamp).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : new Date(row.timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })) : '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">{formatPrice(row.open_price)}</TableCell>
                                                <TableCell align="right" sx={{ color: '#10b981' }}>{formatPrice(row.high_price)}</TableCell>
                                                <TableCell align="right" sx={{ color: '#ef4444' }}>{formatPrice(row.low_price)}</TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" fontWeight="bold" sx={{ color: isPositive ? '#10b981' : '#ef4444' }}>
                                                        {formatPrice(row.close_price)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">{formatVolume(row.volume)}</TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}
        </Box>
    );
};

export default HistoricalDataPage;
